/**
 * Label Layout Editor Types
 *
 * Frontend types extending shared schemas with editor-specific fields.
 * Includes sample data scenarios, design tokens, and component constraints.
 */

// Re-export shared types for consistency
export {
  CANVAS_PRESETS,
  CanvasSizeSchema,
  ComponentTypeSchema,
  LabelTypeSchema,
  ComponentConfigSchema,
  LabelTemplateSchema,
  COMPONENT_AVAILABILITY,
  isComponentAvailableForLabelType,
  validateLabelLayout,
  type CanvasPresetKey,
  type CanvasSize,
  type ComponentType,
  type LabelType,
  type ComponentConfig,
  type LabelTemplate,
  type LabelValidationError,
  type LabelValidationResult,
} from '@threadheads/shared';

// ============================================================================
// COORDINATE CONVERSION CONSTANTS
// ============================================================================

/** Millimeters per inch (standard conversion) */
export const MM_PER_INCH = 25.4;

/** CSS pixels per inch (CSS spec defines 1in = 96px) */
export const CSS_PPI = 96;

/** Pixels per millimeter at 96 DPI (~3.78) */
export const PX_PER_MM = CSS_PPI / MM_PER_INCH;

// ============================================================================
// COMPONENT CONSTRAINTS
// ============================================================================

import type { ComponentType, ComponentConfig, CanvasSize, LabelType, LabelValidationError } from '@threadheads/shared';
import { validateLabelLayout as sharedValidateLabelLayout } from '@threadheads/shared';

/**
 * Constraints for component sizing and positioning.
 * These are enforced in the editor and validated on save.
 */
export interface ComponentConstraints {
  /** Minimum width in mm */
  minWidth?: number;
  /** Minimum height in mm */
  minHeight?: number;
  /** Maximum width in mm */
  maxWidth?: number;
  /** Maximum height in mm */
  maxHeight?: number;
  /** Aspect ratio (width/height), e.g., 1 for square */
  aspectRatio?: number;
  /** Cannot be hidden */
  required?: boolean;
  /** Required for specific label types */
  requiredFor?: ('POD' | 'P&P')[];
  /**
   * Label types this component is available for.
   * If not specified, component is available for all types.
   * Components unavailable for the current label type are disabled in the panel.
   */
  availableFor?: ('POD' | 'P&P' | 'BOTH')[];
}

/**
 * Constraints per component type - MATCHES BACKEND.
 * Minimum sizes ensure readability and scannability.
 *
 * availableFor rules:
 * - binLabel: Only P&P and BOTH (POD-only bins assigned at catcher, AFTER printing)
 * - dtgIndicator: Only POD and BOTH (P&P items don't go through DTG)
 * - ptBadge: Only POD and BOTH (pretreat only needed for DTG on dark garments)
 * - printArea: Only POD and BOTH (Phase 104, D-09: only printed garments have one)
 */
export const COMPONENT_CONSTRAINTS: Record<ComponentType, ComponentConstraints> = {
  qr: {
    minWidth: 15,
    minHeight: 15,
    aspectRatio: 1,
    required: true,
  },
  barcode: {
    minWidth: 30,
    minHeight: 6,
  },
  orderNumberBarcode: {
    minWidth: 25,
    minHeight: 6,
  },
  orderNumber: { minHeight: 4 },
  sizeColor: { minHeight: 4 },
  productInfo: { minHeight: 8 },
  itemCount: { minHeight: 4 },
  binLabel: {
    minHeight: 4,
    availableFor: ['P&P', 'BOTH'],  // NOT POD - bin assigned at catcher AFTER printing
  },
  productType: { minHeight: 4 },
  checkboxes: { minWidth: 10, minHeight: 8 },
  dtgIndicator: {
    minWidth: 8,
    minHeight: 4,
    availableFor: ['POD', 'BOTH'],  // NOT P&P - no DTG printing
  },
  ptBadge: {
    minWidth: 6,
    minHeight: 4,
    availableFor: ['POD', 'BOTH'],  // NOT P&P - no pretreatment needed
  },
  redoBadge: { minWidth: 10, minHeight: 4 },
  printArea: {
    minWidth: 20,
    minHeight: 6,
    availableFor: ['POD', 'BOTH'],  // NOT P&P - nothing is printed
  },
};

/**
 * i18n key paths for component labels.
 * Used with t(COMPONENT_LABEL_KEYS[type]) in consuming components.
 */
export const COMPONENT_LABEL_KEYS: Record<ComponentType, string> = {
  qr: 'production.componentLabels.qr',
  barcode: 'production.componentLabels.barcode',
  orderNumberBarcode: 'production.componentLabels.orderNumberBarcode',
  orderNumber: 'production.componentLabels.orderNumber',
  sizeColor: 'production.componentLabels.sizeColor',
  productInfo: 'production.componentLabels.productInfo',
  itemCount: 'production.componentLabels.itemCount',
  binLabel: 'production.componentLabels.binLabel',
  productType: 'production.componentLabels.productType',
  checkboxes: 'production.componentLabels.checkboxes',
  dtgIndicator: 'production.componentLabels.dtgIndicator',
  ptBadge: 'production.componentLabels.ptBadge',
  redoBadge: 'production.componentLabels.redoBadge',
  printArea: 'production.componentLabels.printArea',
};

// ============================================================================
// EDITOR STATE
// ============================================================================

/**
 * Editor UI state - NOT persisted in undo history.
 */
export interface EditorState {
  /** Currently selected component ID */
  selectedComponentId: string | null;
  /** Zoom level, default 3 (or 2 on mobile) */
  scale: number;
  /** Show alignment grid */
  showGrid: boolean;
  /** Snap to grid when dragging */
  snapToGrid: boolean;
  /** Grid size in mm (default 5mm) */
  gridSizeMm: number;
  /** Which sample data scenario to show */
  previewScenario: SampleScenarioKey;
}

// ============================================================================
// SAMPLE DATA SCENARIOS
// ============================================================================

/**
 * Available sample data scenarios for preview testing.
 */
export type SampleScenarioKey = 'standard' | 'redo' | 'longName' | 'pAndP';

/**
 * Sample garment data structure for preview rendering.
 * Matches the data shape used by actual label components.
 */
export interface SampleGarmentData {
  id: string;
  orderNumber: string;
  sku: string;
  productName: string;
  size: string;
  color: string;
  requiresPretreat: boolean;
  fulfillmentType: 'POD' | 'P&P';
  itemNumber: number;
  totalItems: number;
  binLabel: string;
  isRedo: boolean;
  shopifyProductType: string | null;
  /**
   * Phase 104 (D-09): print-area lines of a manual order's unit
   * ("FRONT LEFT CHEST"); empty or absent for every other unit.
   */
  printArea?: readonly string[];
}

/**
 * Sample data scenarios for testing different label content.
 * These simulate real production scenarios.
 */
export const SAMPLE_SCENARIOS: Record<SampleScenarioKey, SampleGarmentData> = {
  standard: {
    id: '00000000-0000-0000-0000-000000000001',
    orderNumber: '974558',
    sku: 'TSHIRT-BLK-001',
    productName: 'Classic Tee',
    size: 'XL',
    color: 'BLACK',
    requiresPretreat: true,
    fulfillmentType: 'POD',
    itemNumber: 2,
    totalItems: 4,
    binLabel: 'BIN-042',
    isRedo: false,
    shopifyProductType: 'T-Shirt',
    printArea: ['FRONT LEFT CHEST', 'BACK FULL BACK'],
  },
  redo: {
    id: '00000000-0000-0000-0000-000000000002',
    orderNumber: '974558',
    sku: 'TSHIRT-WHT-002',
    productName: 'Premium Hoodie',
    size: 'M',
    color: 'WHITE',
    requiresPretreat: false,
    fulfillmentType: 'POD',
    itemNumber: 1,
    totalItems: 1,
    binLabel: '',
    isRedo: true,
    shopifyProductType: 'Hoodie',
  },
  longName: {
    id: '00000000-0000-0000-0000-000000000003',
    orderNumber: '1234567890',
    sku: 'HOODIE-NAVY-PREMIUM-XXL-001',
    productName: 'Super Premium Organic Cotton Vintage Wash Hoodie - Limited Edition',
    size: 'XXL',
    color: 'NAVY BLUE',
    requiresPretreat: true,
    fulfillmentType: 'POD',
    itemNumber: 5,
    totalItems: 12,
    binLabel: 'BIN-999',
    isRedo: false,
    shopifyProductType: 'Long Sleeve Henley T-Shirt',
    printArea: ['FRONT CENTRE CHEST', 'BACK UPPER BACK'],
  },
  pAndP: {
    id: '00000000-0000-0000-0000-000000000004',
    orderNumber: '555123',
    sku: 'TANK-RED-001',
    productName: 'Muscle Tank',
    size: 'L',
    color: 'RED',
    requiresPretreat: false,
    fulfillmentType: 'P&P',
    itemNumber: 3,
    totalItems: 3,
    binLabel: 'BIN-007',
    isRedo: false,
    shopifyProductType: 'Tank Top',
  },
};

/** Default sample data for initial preview */
export const DEFAULT_SAMPLE_DATA = SAMPLE_SCENARIOS.standard;

// ============================================================================
// VALIDATION RESULT
// ============================================================================

/**
 * Validation result from backend or real-time editor checks.
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  /** Structured errors with code for i18n translation */
  structuredErrors: LabelValidationError[];
  /** Structured warnings with code for i18n translation */
  structuredWarnings: LabelValidationError[];
}

// ============================================================================
// DESIGN TOKENS
// ============================================================================

/**
 * Design token references for consistent styling.
 * Uses CSS variables defined in Tailwind config for theme awareness.
 */
export const DESIGN_TOKENS = {
  selection: {
    border: 'hsl(var(--primary))',
    ring: 'hsl(var(--primary) / 0.2)',
    background: 'hsl(var(--primary) / 0.05)',
  },
  locked: {
    border: 'hsl(var(--amber-400, 251 191 36))', // amber-400 fallback
    background: 'hsl(var(--amber-50, 255 251 235))',
    icon: 'hsl(var(--amber-500, 245 158 11))',
  },
  hidden: {
    border: 'hsl(var(--muted-foreground) / 0.3)',
    background: 'hsl(var(--muted) / 0.5)',
  },
  grid: {
    line: 'hsl(var(--border))',
  },
  canvas: {
    background: 'hsl(var(--background))',
    border: 'hsl(var(--border))',
  },
} as const;

// ============================================================================
// CLIENT-SIDE VALIDATION
// ============================================================================

/**
 * Validate component layout for scannability and completeness.
 * Provides immediate feedback in the editor before saving.
 *
 * Uses the shared validateLabelLayout function from @threadheads/shared
 * to ensure consistent validation between frontend and backend.
 */
export function validateScannability(
  components: ComponentConfig[],
  canvasSize: CanvasSize,
  labelType: LabelType
): ValidationResult {
  // Use shared validation function for consistency
  const result = sharedValidateLabelLayout(components, canvasSize, labelType);

  return {
    valid: result.valid,
    errors: result.errors.map((e: LabelValidationError) => e.message),
    warnings: result.warnings.map((w: LabelValidationError) => w.message),
    structuredErrors: result.errors,
    structuredWarnings: result.warnings,
  };
}
